import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
  sidebarOpen: boolean;
  sidebarHoverExpanded: boolean;
  mobileMenuOpen: boolean;
  theme: 'light' | 'dark';
  notifications: Notification[];
  modal: ModalState;
}

export type UIModalId =
  | 'onboarding.checklist'
  | 'global.search'
  | 'commandCenter.settings'
  | 'settings.products.package'
  | 'settings.products.discount'
  | 'invoicing.discounts.editor'
  | 'settings.contracts.editor'
  | 'settings.clientPortal.preview'
  | 'settings.quotes.editor'
  | 'settings.invoice.editor'
  | 'settings.questionnaires.editor'
  | 'settings.emails.editor'
  | 'settings.invoice.schedule'
  | 'invoicing.paymentPlans.editor'
  | 'settings.taxRates.editor'
  | 'projects.forms.add'
  | 'forms.add'
  | 'forms.create-template';

interface ModalState {
  activeModal: UIModalId | null;
  payload: Record<string, string | number | boolean | null> | null;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

const initialState: UIState = {
  sidebarOpen: false,
  sidebarHoverExpanded: false,
  mobileMenuOpen: false,
  theme: 'light',
  notifications: [],
  modal: {
    activeModal: null,
    payload: null,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: state => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setSidebarHoverExpanded: (state, action: PayloadAction<boolean>) => {
      state.sidebarHoverExpanded = action.payload;
    },
    toggleMobileMenu: state => {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.push(action.payload);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: state => {
      state.notifications = [];
    },
    openModal: (
      state,
      action: PayloadAction<{
        id: UIModalId;
        payload?: Record<string, string | number | boolean | null>;
      }>
    ) => {
      state.modal.activeModal = action.payload.id;
      state.modal.payload = action.payload.payload ?? null;
    },
    closeModal: state => {
      state.modal.activeModal = null;
      state.modal.payload = null;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  setSidebarHoverExpanded,
  toggleMobileMenu,
  setMobileMenuOpen,
  setTheme,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
} = uiSlice.actions;
export default uiSlice.reducer;
